import React, { useState, useEffect, useContext } from "react";
import { AuthService } from "../../api/AuthService";
import { RBCardView } from "../../components/RBCardView";
import { Spacing, Colors } from "../../theme/Theme";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  InputBase,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { EmptyString, RoutePathConstant, roleNameEnum } from "../../util/Common";
import NotificationService, {
  NotificationServiceTopic,
} from "../../api/NotificationService";
import { RBContext } from "../../components/RBContext";
import { IndustryModeEnum } from "../../api/constants";
import { useHistory } from "react-router-dom";
import {
  getRegionalManagerLocationDisplayLabel,
  getRegionalManagerOwnerDisplayName,
  RegionalManagerDashboardResponse,
  RegionalManagerDashboardRow,
} from "../../interfacesProps/RegionalManagerDashboardProps";

export const RegionalManagerDashboard = () => {
  // Section: local UI and data state.
  const [selectedLocationTitle, setSelectedLocationTitle] = useState(EmptyString);
  const [refresh, setRefresh] = useState(false);
  const [dashboardRows, setDashboardRows] = useState<RegionalManagerDashboardRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasAutoLoadedAllVisiblePages, setHasAutoLoadedAllVisiblePages] = useState(false);
  const [searchTerm, setSearchTerm] = useState(EmptyString);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(EmptyString);

  const { industryMode, primaryColor } = useContext(RBContext);
  const history = useHistory();
  const roleName = AuthService.getRoleName();
  const tableContainerRef = React.useRef<HTMLDivElement | null>(null);

  const fetchData = async (pageNumber: number, shouldAppend = false) => {
    setIsLoading(true);
    try {
      const response: RegionalManagerDashboardResponse =
        await AuthService.getRegionalManagerDashboardView(
          12,
          pageNumber,
          debouncedSearchTerm.trim()
        );
      const rows = response?.data || [];

      setDashboardRows((prevRows) =>
        shouldAppend ? [...prevRows, ...rows] : rows
      );
      setPage(response?.pageInfo?.page || pageNumber);
      setTotalPages(response?.pageInfo?.pages || 1);

      if (!shouldAppend) {
        setSelectedLocationTitle(
          rows.length > 0 ? getRegionalManagerLocationDisplayLabel(rows[0]) : EmptyString
        );
        setHasAutoLoadedAllVisiblePages(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Section: refresh trigger when account context changes elsewhere.
  const onAccountChanged = () => {
    setRefresh((pre) => !pre);
  };

  // Section: initial load and refresh-driven data sync.
  useEffect(() => {
    const debounceTimer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 350);

    return () => {
      window.clearTimeout(debounceTimer);
    };
  }, [searchTerm]);

  useEffect(() => {
    const unsub = NotificationService.subscribe(
      NotificationServiceTopic.AccountChanged,
      onAccountChanged
    );

    setDashboardRows([]);
    setPage(1);
    setTotalPages(1);
    setSelectedLocationTitle(EmptyString);
    setHasAutoLoadedAllVisiblePages(false);
    fetchData(1);
    return () => {
      unsub.unsubscribe();
    };
  }, [refresh, debouncedSearchTerm]);

  useEffect(() => {
    const container = tableContainerRef.current;

    if (
      !container ||
      isLoading ||
      hasAutoLoadedAllVisiblePages ||
      page >= totalPages
    ) {
      return;
    }

    const hasScrollableOverflow = container.scrollHeight > container.clientHeight;
    if (hasScrollableOverflow) {
      setHasAutoLoadedAllVisiblePages(true);
      return;
    }

    fetchData(page + 1, true);
  }, [dashboardRows, isLoading, page, totalPages, hasAutoLoadedAllVisiblePages]);

  const title =
    industryMode === IndustryModeEnum.Pharmaceutical
      ? "pharmacy"
      : industryMode === IndustryModeEnum.Dentistry
      ? "dental practice"
      : "company";

  const handleTableScroll = async (
    event: React.UIEvent<HTMLDivElement, UIEvent>
  ) => {
    const target = event.currentTarget;
    const reachedBottom =
      target.scrollTop + target.clientHeight >= target.scrollHeight - 24;

    if (!reachedBottom || isLoading || page >= totalPages) {
      return;
    }

    await fetchData(page + 1, true);
  };

  // Section: page layout and interactive table UI.
  return (
    <>
      <RBCardView
        style={{
          margin: Spacing.large,
          padding: Spacing.large,
          minHeight: "100%",
        }}
      >
        <Typography
          variant={"h6"}
          style={{ textAlign: "center", marginBottom: Spacing.large }}
        >
          Select a location
        </Typography>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            border: `1px solid ${Colors.mediumGrey}`,
            borderRadius: 999,
            background: Colors.white,
            padding: "6px 14px",
            marginBottom: Spacing.medium,
            boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
          }}
        >
          <SearchIcon
            style={{
              color: primaryColor,
              fontSize: 20,
              marginRight: 10,
            }}
          />
          <InputBase
            fullWidth
            placeholder="Search by owner or location name"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            style={{ fontSize: 14 }}
          />
        </div>

        {/* Scrollable stats table for all owner locations. */}
        <TableContainer
          component={Paper}
          ref={tableContainerRef}
          onScroll={handleTableScroll}
          style={{
            border: `1px solid ${Colors.mediumGrey}`,
            maxHeight: 420,
            marginTop: Spacing.medium,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  style={{
                    background: primaryColor || "#0F5F85",
                    color: Colors.white,
                    fontWeight: 700,
                  }}
                >
                  Owner - Location
                </TableCell>
                <TableCell
                  style={{
                    background: primaryColor || "#0F5F85",
                    color: Colors.white,
                    fontWeight: 700,
                  }}
                >
                  Future Shifts
                </TableCell>
                <TableCell
                  style={{
                    background: primaryColor || "#0F5F85",
                    color: Colors.white,
                    fontWeight: 700,
                  }}
                >
                  Unfilled
                </TableCell>
                <TableCell
                  style={{
                    background: primaryColor || "#0F5F85",
                    color: Colors.white,
                    fontWeight: 700,
                  }}
                >
                  WithApplicants
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {!isLoading && dashboardRows.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    align="center"
                    style={{ color: Colors.darkGrey, padding: "24px 12px" }}
                  >
                    No matching locations found.
                  </TableCell>
                </TableRow>
              )}
              {dashboardRows.map((row: RegionalManagerDashboardRow) => {
                const displayLabel = getRegionalManagerLocationDisplayLabel(row);
                const isSelected = selectedLocationTitle === displayLabel;
                return (
                  // Clicking a row marks that location as the active selection.
                  <TableRow
                    key={row.locationId}
                    hover
                    onClick={() => {
                      setSelectedLocationTitle(displayLabel);
                      if (roleName === roleNameEnum.regionalmanager) {
                        localStorage.setItem(
                          "regionalManagerSelectedUserId",
                          row.ownerUserId
                        );
                        localStorage.setItem(
                          "regionalManagerSelectedLocationId",
                          row.locationId
                        );
                        localStorage.setItem(
                          "regionalManagerSelectedLocationTitle",
                          displayLabel
                        );
                        localStorage.setItem(
                          "regionalManagerSelectedOwnerName",
                          getRegionalManagerOwnerDisplayName(row)
                        );
                        localStorage.setItem(
                          "defaultPharmacyId",
                          row.locationId
                        );
                        localStorage.setItem(
                          "pharmacyName",
                          row.pharmacy?.name || row.locationTitleName
                        );
                        window.dispatchEvent(
                          new Event("regional-manager-location-changed")
                        );
                        history.push(RoutePathConstant.DashBoard);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      background: isSelected ? "#ECF3F8" : undefined,
                    }}
                  >
                    <TableCell style={{ fontWeight: 500 }}>
                      <div>{displayLabel}</div>
                    </TableCell>
                    <TableCell>{row.futureShiftCounts ?? 0}</TableCell>
                    <TableCell>{row.unfilledShiftCounts ?? 0}</TableCell>
                    <TableCell>{row.withApplicantsShiftCounts ?? 0}</TableCell>
                  </TableRow>
                );
              })}
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
                      <CircularProgress size={18} style={{ color: primaryColor }} />
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </RBCardView>
    </>
  );
};
